import React from 'react';
import { useIntl } from 'react-intl';
import Steven, { ELEMENT_TYPES } from 'helpers/steven';
import CCModal from 'components/shared/DesignSystem/CCModal/CCModal';
import CompanyDashboardGraphic from 'images/CompanyDashboardGraphic.png';

import messages from './messages.ts';
import {
  ContentCard,
  ImageWrapper,
  CardText,
  CardTitle,
  FeatureList,
  FeatureListItem,
  FooterContentStyle,
} from './StyledComponents.ts';

import { Text } from '@companycam/slab-web';

type CompanyDashboardModalProps = {
  onRequestClose: () => void;
  isAdmin?: boolean;
};

type Action = {
  label: string;
  onClick: () => void;
  disabled: boolean;
};

const CompanyDashboardModal: React.FC<CompanyDashboardModalProps> = ({
  onRequestClose,
  isAdmin,
}) => {
  const intl = useIntl();

  const primaryAction: Action = {
    label: intl.formatMessage(
      isAdmin ? messages.upgradeButtonText : messages.okButtonText,
    ),
    onClick: () => {
      const actionId = isAdmin
        ? 'company_dashboard_modal_upgrade_button'
        : 'company_dashboard_modal_ok_button';
      const content = isAdmin
        ? 'Company Dashboard Modal Upgrade Button Clicked'
        : 'Company Dashboard Modal OK Button Clicked';
      const actionName = isAdmin
        ? 'company_dashboard_modal_upgrade_button_clicked'
        : 'company_dashboard_modal_ok_button_clicked';

      Steven.elementClicked({
        id: actionId,
        content,
        type: ELEMENT_TYPES.BUTTON,
        action: actionName,
      });

      if (isAdmin) {
        window.Turbo.visit('/billing');
      } else {
        onRequestClose();
      }
    },
    disabled: false,
  };

  const secondaryAction: Action | undefined = isAdmin
    ? {
        label: intl.formatMessage(messages.notNowButtonText),
        onClick: () => {
          Steven.elementClicked({
            id: 'company_dashboard_modal_not_now_button',
            content: 'Company Dashboard Modal Not Now Button Clicked',
            type: ELEMENT_TYPES.BUTTON,
            action: 'company_dashboard_modal_not_now_button_clicked',
          });

          onRequestClose();
        },
        disabled: false,
      }
    : undefined;

  const footerContentMessage = isAdmin
    ? messages.premiumUpgradeMessage
    : messages.premiumAdMessage;

  return (
    <CCModal
      size="small"
      isOpen
      onRequestClose={onRequestClose}
      primaryAction={primaryAction}
      secondaryAction={secondaryAction}
      footerContentBefore={
        <FooterContentStyle>
          <Text size="s" as="p" ccMargin="none">
            {intl.formatMessage(footerContentMessage)}
          </Text>
        </FooterContentStyle>
      }
    >
      <ContentCard>
        <ImageWrapper>
          <img src={CompanyDashboardGraphic} alt="Company Dashboard" />
        </ImageWrapper>

        <CardText>
          <CardTitle>
            <Text size="xxl" as="h1" ccMargin="none">
              {intl.formatMessage(messages.modalTitle)}
            </Text>
            <Text size="l" as="h1" ccMargin="none">
              {intl.formatMessage(messages.introMessage)}
            </Text>
          </CardTitle>

          <FeatureList>
            <FeatureListItem>
              <i className="mdi mdi-check" />
              <Text size="s" as="p" ccMargin="none">
                {intl.formatMessage(messages.feature1)}
              </Text>
            </FeatureListItem>
            <FeatureListItem>
              <i className="mdi mdi-check" />
              <Text size="s" as="p" ccMargin="none">
                {intl.formatMessage(messages.feature2)}
              </Text>
            </FeatureListItem>
            <FeatureListItem>
              <i className="mdi mdi-check" />
              <Text size="s" as="p" ccMargin="none">
                {intl.formatMessage(messages.feature3)}
              </Text>
            </FeatureListItem>
          </FeatureList>
        </CardText>
      </ContentCard>
    </CCModal>
  );
};

export default CompanyDashboardModal;
